import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import Badge from "../ui/Badge";
import Button from "../ui/Button";
import { historyService } from "../../services/history";
import { requestBodyScrollLock } from "../../utils/bodyScrollLock";
import { getScoreBadgeClass } from "../../utils/scoreColors";

const COMPANY_FIELD_LABELS = {
  company_name: "Company",
  summary: "Summary",
  industry: "Industry",
  company_size_hint: "Company size",
  business_model: "Business model",
  sales_motion: "Sales motion",
  offer_category: "Offer category",
  geo_focus: "Geo focus",
  company_maturity: "Maturity",
  buyer_persona: "Buyer persona",
  pricing_notes: "Pricing notes",
  target_audience: "Target audience",
  products: "Products / services",
  differentiators: "Differentiators",
};

const COMPANY_FIELD_ORDER = [
  "company_name",
  "summary",
  "industry",
  "company_size_hint",
  "business_model",
  "sales_motion",
  "offer_category",
  "geo_focus",
  "company_maturity",
  "buyer_persona",
  "pricing_notes",
  "target_audience",
  "products",
  "differentiators",
];

function formatSentAt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

function companyRoleLine(companyRaw, roleRaw) {
  const company = (companyRaw || "").trim();
  const role = (roleRaw || "").trim();
  if (company && role) return `${company} | ${role}`;
  return company || role || "";
}

function ExpandableText({ text, className = "", collapsedLines = 2 }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const clampClass = collapsedLines === 3 ? "line-clamp-3" : "line-clamp-2";
  return (
    <p
      role={open ? undefined : "button"}
      tabIndex={open ? -1 : 0}
      onKeyDown={(e) => {
        if (!open && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          setOpen(true);
        }
      }}
      className={`text-sm text-theme-text leading-relaxed ${open ? "cursor-text" : `cursor-pointer ${clampClass}`} ${className}`}
      onClick={() => {
        if (!open) setOpen(true);
      }}
    >
      {text}
    </p>
  );
}

function ClassificationCard({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-theme-border bg-theme-main/80 px-4 py-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim">{label}</div>
      <div className="mt-1 text-sm text-theme-text font-medium">{value}</div>
      {sub ? <div className="mt-1 text-xs text-theme-dim">{sub}</div> : null}
    </div>
  );
}

function sortedExtractedEntries(obj) {
  if (!obj || typeof obj !== "object") return [];
  const keys = Object.keys(obj);
  const ranked = [];
  for (const k of COMPANY_FIELD_ORDER) {
    if (keys.includes(k) && obj[k] != null && obj[k] !== "") ranked.push(k);
  }
  for (const k of keys) {
    if (!ranked.includes(k)) ranked.push(k);
  }
  return ranked.map((k) => [k, obj[k]]);
}

const EmailDetailDrawer = ({ inboundHash, brandHash = "", onClose }) => {
  const navigate = useNavigate();
  const detailQuery = useQuery({
    queryKey: ["history-detail", inboundHash, brandHash],
    queryFn: () => historyService.getDetail(inboundHash, brandHash),
    enabled: Boolean(inboundHash),
  });

  const d = detailQuery.data;
  const [originalOpen, setOriginalOpen] = useState(false);
  const originalSectionRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const stickyLeadRef = useRef(null);

  useEffect(() => {
    setOriginalOpen(false);
  }, [inboundHash]);

  useEffect(() => {
    if (!originalOpen) return;
    requestAnimationFrame(() => {
      const container = scrollContainerRef.current;
      const section = originalSectionRef.current;
      if (!container || !section) return;
      const stickyHeight = stickyLeadRef.current?.getBoundingClientRect().height || 0;
      const containerTop = container.getBoundingClientRect().top;
      const sectionTop = section.getBoundingClientRect().top;
      const delta = sectionTop - containerTop - stickyHeight - 8;
      container.scrollTo({ top: container.scrollTop + delta, behavior: "smooth" });
    });
  }, [originalOpen]);

  useEffect(() => {
    if (!inboundHash) return undefined;
    const releaseScroll = requestBodyScrollLock();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      releaseScroll();
      window.removeEventListener("keydown", onKey);
    };
  }, [inboundHash, onClose]);

  const senderCompany = (d?.sender_company || "").trim();
  const senderPerson = (d?.sender_name || "").trim();
  const aboutCompanyName = senderCompany || senderPerson || "Company";
  const headerCompanyRole = d ? companyRoleLine(d.sender_company, d.sender_role_title) : "";
  const headerTitle = detailQuery.isLoading
    ? "Loading…"
    : senderPerson || (d?.sender_email || "").trim() || "Message";

  const fromEmail = (d?.sender_email || "").trim();
  const toMailbox = (d?.mailbox_email || "").trim();
  const companyUrl = (d?.company_website_url || "").trim();
  const inboxUrl = (d?.inbox_url || "").trim();
  const brandLabelShort = ((d?.brand_label || "").trim() || "Brand").trim();

  const showMatchSection =
    d &&
    (d.match_score != null ||
      (d.score_match_reason || "").trim() ||
      (d.pitch_summary || "").trim() ||
      d.company_match_score != null ||
      d.role_match_score != null);

  const extractedRaw = d?.company_extracted && typeof d.company_extracted === "object" ? d.company_extracted : {};
  const sellSummary = (d?.sell_summary || "").trim();
  const extractedSummary = typeof extractedRaw.summary === "string" ? extractedRaw.summary.trim() : "";
  const showExtractedSummary = !sellSummary && extractedSummary;

  const extractedForGrid = { ...extractedRaw };
  delete extractedForGrid.summary;
  const extractedEntries = sortedExtractedEntries(extractedForGrid).filter(([key]) => {
    const v = extractedForGrid[key];
    if (v == null || v === "") return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  });

  const drawer = (
    <div
      className="fixed inset-0 z-[100] flex min-h-[100vh] min-h-[100dvh] w-full max-w-none justify-end"
      aria-modal="true"
      role="dialog"
    >
      <style>{`
        @keyframes historyDrawerIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
      <button
        type="button"
        aria-label="Close detail"
        className="absolute inset-0 min-h-full w-full bg-black/40 backdrop-blur-[1px]"
        onClick={onClose}
      />
      <aside
        className="relative z-10 flex h-full min-h-0 w-[min(36rem,100vw)] max-h-[100vh] max-h-[100dvh] flex-col border-l border-theme-border bg-theme-main shadow-2xl"
        style={{ animation: "historyDrawerIn 0.28s ease-out both" }}
      >
        <header className="border-b border-theme-border px-5 py-4 shrink-0 space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim">Mailbox history</p>
          <div className="flex min-w-0 items-center gap-3">
            <h2 className="min-w-0 flex-1 font-display font-bold text-xl leading-snug text-theme-on-background break-words">
              {headerTitle}
            </h2>
            <div className="flex shrink-0 items-center gap-2">
              {d?.match_score != null ? (
                <Badge variant="inherit" className={getScoreBadgeClass(d.match_score)}>
                  {brandLabelShort} · {d.match_score}%
                </Badge>
              ) : null}
              <button
                type="button"
                className="rounded-lg border border-theme-border px-3 py-1.5 font-mono text-[11px] text-theme-dim hover:border-theme-border-bright hover:text-theme-text"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
          {headerCompanyRole ? (
            <p className="font-mono text-[11px] leading-relaxed text-theme-dim break-words">{headerCompanyRole}</p>
          ) : null}
          {fromEmail || toMailbox ? (
            <p className="font-mono text-[11px] leading-relaxed text-theme-dim break-words">
              {fromEmail ? <span>{fromEmail}</span> : null}
              {fromEmail && toMailbox ? (
                <span className="mx-1 inline align-middle" aria-hidden>
                  <span
                    className="material-symbols-outlined inline-block align-middle text-theme-dim"
                    style={{ fontSize: "12px", lineHeight: 1, verticalAlign: "middle" }}
                  >
                    arrow_forward
                  </span>
                </span>
              ) : null}
              {toMailbox ? <span>{toMailbox}</span> : null}
            </p>
          ) : null}
          {d?.subject ? (
            <p className="text-[13px] font-semibold text-theme-text opacity-90 break-words">{d.subject}</p>
          ) : null}
        </header>

        <div
          ref={scrollContainerRef}
          className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5 flipmail-drawer-scroll flipmail-scrollbar-hover"
        >
          {detailQuery.isError ? (
            <p className="text-sm text-theme-dim">Could not load this message.</p>
          ) : null}

          {d ? (
            <>
              {d.lead_hash ? (
                <section
                  ref={stickyLeadRef}
                  className="sticky top-[-1rem] z-20 -mx-5 !mt-0 border-y border-theme-border bg-theme-main/75 px-5 py-3 backdrop-blur-md shadow-[0_10px_20px_rgba(0,0,0,0.14)]"
                >
                  <Button
                    type="button"
                    size="sm"
                    className="h-9 px-3 inline-flex items-center gap-1"
                    onClick={() => navigate(`/leads/${d.lead_hash}/`)}
                  >
                    Open lead
                    <span className="material-symbols-outlined text-[16px] leading-none">chevron_right</span>
                  </Button>
                </section>
              ) : null}

              {showMatchSection ? (
                <section className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                    <h3 className="min-w-0 flex-1 font-mono text-[10px] uppercase tracking-[0.1em] text-theme-text opacity-75">
                      {d.match_score != null ? (
                        <>
                          {Number(d.match_score)}% match{d.brand_label ? ` for ${brandLabelShort}` : ""}
                        </>
                      ) : (
                        <>Brand fit</>
                      )}
                    </h3>
                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                      {d.company_match_score != null ? (
                        <Badge variant="inherit" className={getScoreBadgeClass(d.company_match_score)}>
                          {d.company_match_score}% company match
                        </Badge>
                      ) : null}
                      {(d.sender_role_title || "").trim() && d.role_match_score != null ? (
                        <Badge variant="inherit" className={getScoreBadgeClass(d.role_match_score)}>
                          {d.role_match_score}% role match
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                  {(d.score_match_reason || "").trim() ? (
                    <ExpandableText text={d.score_match_reason} collapsedLines={3} />
                  ) : null}
                  {(d.pitch_summary || "").trim() ? (
                    <div className="rounded-lg border border-theme-border bg-theme-main/60 px-3 py-2">
                      <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim">Scorer summary</p>
                      <p className="mt-1 text-sm text-theme-text leading-relaxed whitespace-pre-wrap">{d.pitch_summary}</p>
                    </div>
                  ) : null}
                  {d.score_confidence != null ? (
                    <p className="font-mono text-xs text-theme-dim">
                      Model confidence: {Number(d.score_confidence).toFixed(2)} (0 to 1)
                    </p>
                  ) : null}
                  {d.score_breakdown && Object.keys(d.score_breakdown).length > 0 ? (
                    <details className="rounded-lg border border-theme-border bg-theme-main/60 px-3 py-2">
                      <summary className="cursor-pointer font-mono text-[11px] text-theme-dim">Full score payload</summary>
                      <pre className="mt-2 max-h-48 overflow-auto text-[11px] text-theme-dim whitespace-pre-wrap break-words flipmail-scrollbar-hover">
                        {JSON.stringify(d.score_breakdown, null, 2)}
                      </pre>
                    </details>
                  ) : null}
                </section>
              ) : null}

              <section className="space-y-3">
                <h3 className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-text opacity-75">
                  Classification
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <ClassificationCard
                    label="From human"
                    value={d.is_from_human ? "Yes" : "No"}
                    sub={
                      d.is_from_human
                        ? `${Number(d.is_from_human_confidence ?? 0).toFixed(0)}% confidence`
                        : "Not flagged as person-like sender"
                    }
                  />
                  <ClassificationCard
                    label="Cold pitch"
                    value={d.is_cold_pitch ? "Yes" : "No"}
                    sub={
                      d.is_cold_pitch
                        ? `${Number(d.is_cold_pitch_confidence ?? 0).toFixed(0)}% confidence`
                        : "Not classified as one-to-one pitch"
                    }
                  />
                </div>
                {(d.email_type || "").trim() ? (
                  <p className="font-mono text-[11px] text-theme-dim">
                    Email type: {d.email_type}
                    {(d.email_type_other || "").trim() ? ` (${d.email_type_other.trim()})` : ""}
                  </p>
                ) : null}
              </section>

              {sellSummary || showExtractedSummary || extractedEntries.length > 0 || companyUrl ? (
                <section className="space-y-2">
                  <h3 className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-text opacity-75">
                    About{" "}
                    {companyUrl ? (
                      <a
                        href={companyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-0.5 underline underline-offset-2 hover:opacity-90"
                      >
                        {aboutCompanyName}
                        <span
                          className="material-symbols-outlined inline-block shrink-0 leading-none align-middle"
                          style={{ fontSize: "10px", lineHeight: 1 }}
                          aria-hidden
                        >
                          open_in_new
                        </span>
                      </a>
                    ) : (
                      <span>{aboutCompanyName}</span>
                    )}
                  </h3>
                  {sellSummary ? <ExpandableText text={sellSummary} collapsedLines={3} /> : null}
                  {showExtractedSummary ? <ExpandableText text={extractedSummary} collapsedLines={3} /> : null}
                  {extractedEntries.length > 0 ? (
                    <div className="space-y-3 rounded-lg border border-theme-border bg-theme-main/60 px-3 py-3">
                      <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim">From website research</p>
                      <dl className="space-y-3">
                        {extractedEntries.map(([key, val]) => (
                          <div key={key}>
                            <dt className="font-mono text-[10px] uppercase tracking-[0.08em] text-theme-dim">
                              {COMPANY_FIELD_LABELS[key] || key.replace(/_/g, " ")}
                            </dt>
                            <dd className="mt-1 text-sm text-theme-text leading-relaxed">
                              {Array.isArray(val) ? (
                                <ul className="list-disc pl-4 space-y-1">
                                  {val.map((item, idx) => (
                                    <li key={`${key}-${idx}`}>{item}</li>
                                  ))}
                                </ul>
                              ) : (
                                String(val)
                              )}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  ) : null}
                </section>
              ) : null}

              {(d.cold_pitch_summary || "").trim() ? (
                <section className="space-y-2">
                  <h3 className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-text opacity-75">Their pitch</h3>
                  <ExpandableText text={d.cold_pitch_summary.trim()} />
                </section>
              ) : null}

              <section ref={originalSectionRef} className="space-y-2 pb-6">
                <h3 className="font-mono text-[10px] text-theme-text opacity-75 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="uppercase tracking-[0.1em]">Original message</span>
                  {inboxUrl ? (
                    <a
                      href={inboxUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 underline underline-offset-2 hover:opacity-90 font-normal tracking-normal normal-case text-[11px]"
                    >
                      View in Gmail
                      <span
                        className="material-symbols-outlined inline-block shrink-0 leading-none align-middle"
                        style={{ fontSize: "10px", lineHeight: 1 }}
                        aria-hidden
                      >
                        open_in_new
                      </span>
                    </a>
                  ) : null}
                </h3>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (!originalOpen) setOriginalOpen(true);
                  }}
                  onKeyDown={(e) => {
                    if (!originalOpen && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      setOriginalOpen(true);
                    }
                  }}
                  className={`flipmail-scrollbar-hover rounded-lg border border-theme-border bg-theme-main/80 p-3 transition-[max-height] duration-300 ease-out ${
                    originalOpen ? "max-h-[9999px] overflow-visible cursor-text" : "max-h-40 overflow-hidden cursor-pointer"
                  }`}
                >
                  {d.subject || d.received_at ? (
                    <div className="mb-2 flex items-center justify-between gap-3 border-b border-theme-border pb-2">
                      <p className="min-w-0 truncate text-[12px] font-semibold text-theme-text opacity-80">
                        {d.subject || "No subject"}
                      </p>
                      {d.received_at ? (
                        <p className="shrink-0 font-mono text-[11px] text-theme-dim">{formatSentAt(d.received_at)}</p>
                      ) : null}
                    </div>
                  ) : null}
                  <div>
                    <p
                      className={`text-sm text-theme-text break-words leading-relaxed ${
                        originalOpen ? "whitespace-pre-wrap" : "whitespace-normal line-clamp-6"
                      }`}
                    >
                      {(d.original_email || "").trim() || "No body text."}
                    </p>
                    {!originalOpen ? <p className="mt-1 text-theme-dim">...</p> : null}
                  </div>
                </div>
                <div className="font-mono text-[11px] text-theme-dim space-y-1">
                  <div>Thread: {d.thread_id || "—"}</div>
                  <div className="break-all">Message id: {d.message_id || "—"}</div>
                </div>
              </section>
            </>
          ) : null}
        </div>
      </aside>
    </div>
  );

  if (typeof document === "undefined") {
    return null;
  }
  return createPortal(drawer, document.body);
};

export default EmailDetailDrawer;
